import {
   flexRender,
   getCoreRowModel,
   getFilteredRowModel,
   getPaginationRowModel,
   getSortedRowModel,
   useReactTable,
   type Column,
   type ColumnDef,
   type ColumnFiltersState,
   type RowData,
   type SortingState,
   type Table as TanstackTable,
   type TableOptions,
} from '@tanstack/react-table';
import { isValidElement, useState, type ComponentProps, type ReactNode } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { Button } from './button';
import { FaIcon } from './icon';
import { Input } from './input';

declare module '@tanstack/react-table' {
   // Interface extension; the "unused" type variables are required to match.
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   interface ColumnMeta<TData extends RowData, TValue> {
      /** Extra classes for this column's th and td. */
      className?: string;
      /**
       * The cell holds its own controls; opts out of the stretched row link,
       * whose full-cell overlay would swallow this cell's clicks.
       */
      interactive?: boolean;
      /** Lets the cell's content wrap onto several lines instead of truncating to one. */
      wrap?: boolean;
   }
}

// max-w-[37.5vw] caps the cell width so truncation can kick in
const CELL_BASE_CLASSES =
   'px-3 md:px-4 py-[13.25px] text-left tabular-nums relative max-lg:max-w-[37.5vw]';

const ROW_LINK_CLASSES =
   'group [&_a]:no-underline [&_a]:transition-colors [&_a]:duration-150 transition-colors duration-150';

// Stretches a cell's anchor into a full-cell click target on row hover.
const STRETCHED_LINK_CELL_CLASSES =
   "group-hover:[&_a]:text-primary group-hover:[&_a]:before:content-[''] group-hover:[&_a]:before:cursor-inherit group-hover:[&_a]:before:block group-hover:[&_a]:before:absolute group-hover:[&_a]:before:inset-0 group-hover:[&_a]:before:z-0";

function Table({ className, ...props }: ComponentProps<'table'>) {
   // A scroll container with no tab stop cannot be scrolled without a pointer,
   // and a table of plain text cells has no focusable child to drag it along.
   return (
      <div
         data-slot="table-container"
         tabIndex={0}
         className="border-1 border-border rounded-2xl overflow-x-auto"
      >
         <table data-slot="table" className={cn('w-full', className)} {...props} />
      </div>
   );
}

function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
   return (
      <thead
         data-slot="table-header"
         className={cn('bg-gray-50 dark:bg-muted', className)}
         {...props}
      />
   );
}

function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
   return (
      <tbody
         data-slot="table-body"
         className={cn('[&_tr]:border-t-1 [&_tr]:border-border', className)}
         {...props}
      />
   );
}

function TableRow({ className, ...props }: ComponentProps<'tr'>) {
   return <tr data-slot="table-row" className={className} {...props} />;
}

function TableHead({ className, ...props }: ComponentProps<'th'>) {
   return <th data-slot="table-head" className={cn(CELL_BASE_CLASSES, className)} {...props} />;
}

function TableCell({ className, ...props }: ComponentProps<'td'>) {
   return <td data-slot="table-cell" className={cn(CELL_BASE_CLASSES, className)} {...props} />;
}

/**
 * The sort icon renders aria-hidden, so aria-sort carries the direction. An
 * idle sortable column reports "none" so it still announces as sortable.
 */
function ariaSort<TData, TValue>(
   column: Column<TData, TValue>
): 'ascending' | 'descending' | 'none' | undefined {
   if (!column.getCanSort()) {
      return undefined;
   }
   const sorted = column.getIsSorted();
   if (sorted === 'asc') {
      return 'ascending';
   }
   if (sorted === 'desc') {
      return 'descending';
   }
   return 'none';
}

/**
 * Header content for a sortable column: renders the sort toggle when the
 * column definition sets `enableSorting: true`, plain text otherwise.
 */
function DataTableColumnHeader<TData, TValue>({
   column,
   children,
   sortLabel,
   className,
}: {
   column: Column<TData, TValue>;
   children: ReactNode;
   /**
    * Accessible name for the sort toggle. This package has no i18n, so callers
    * with a translator should pass a translated string.
    */
   sortLabel?: string;
   className?: string;
}) {
   if (!column.getCanSort()) {
      return <span className={cn('block max-w-full truncate', className)}>{children}</span>;
   }
   const sorted = column.getIsSorted();
   const label = sortLabel ?? (typeof children === 'string' ? `Sort by ${children}` : 'Sort');
   return (
      <button
         type="button"
         className={cn(
            'inline-flex max-w-full items-center gap-1 truncate rounded-sm outline-none hover:text-brand-600 dark:hover:text-brand-300',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            className
         )}
         title={label}
         aria-label={label}
         onClick={column.getToggleSortingHandler()}
      >
         <span className="truncate">{children}</span>
         {sorted === false ? (
            <FaIcon name="sort" className="size-3 shrink-0 text-gray-400" />
         ) : (
            <FaIcon
               name={sorted === 'asc' ? 'arrow-up' : 'arrow-down'}
               className="size-3 shrink-0 text-primary"
            />
         )}
      </button>
   );
}

/**
 * `ColumnDef` types accessor values as `unknown`, so a Date column type-checks
 * and would otherwise throw "Objects are not valid as a React child".
 */
function renderCellValue(value: unknown): ReactNode {
   if (value == null) {
      return '';
   }
   return isValidElement(value) ? value : String(value);
}

interface DataTablePagerLabels {
   previous?: string;
   next?: string;
   page?: (pageNumber: number, pageCount: number) => string;
}

const DEFAULT_PAGER_LABELS = {
   previous: 'Previous',
   next: 'Next',
   page: (pageNumber: number, pageCount: number) => `Page ${pageNumber} of ${pageCount}`,
} satisfies Required<DataTablePagerLabels>;

interface DataTableBaseProps<TData> {
   columns: ColumnDef<TData>[];
   data: TData[];
   getRowId?: TableOptions<TData>['getRowId'];
   /** Show at most this many rows, with no pager; pair with viewAllUrl for the rest. */
   maxRows?: number;
   viewAllUrl?: string;
   viewAllText?: string;
   /** Paginate client-side with this many rows per page. Wins over maxRows. */
   pageSize?: number;
   /**
    * Pager button and status strings. This package has no i18n, so callers with
    * a translator should pass translated strings.
    */
   pagerLabels?: DataTablePagerLabels;
   emptyText?: string;
   /** Extra attrs (e.g. id, a highlight className) for a body row's tr. */
   rowProps?: (row: TData) => Omit<ComponentProps<'tr'>, 'key' | 'ref' | 'children'>;
}

/**
 * The filter input is a bare text field, so it needs an accessible name:
 * filterLabel is required exactly when filterColumnId turns the input on.
 */
type DataTableFilterProps =
   | {
        /** Render a text input that filters this column's values. */
        filterColumnId: string;
        /**
         * Accessible name for the filter input. This package has no i18n, so
         * callers with a translator should pass a translated string.
         */
        filterLabel: string;
        filterPlaceholder?: string;
     }
   | { filterColumnId?: undefined; filterLabel?: undefined; filterPlaceholder?: undefined };

type DataTableProps<TData> = DataTableBaseProps<TData> & DataTableFilterProps;

/**
 * Client-side table on TanStack Table v8. Columns opt into sorting with
 * `enableSorting: true` plus a `DataTableColumnHeader` header.
 */
function DataTable<TData>({
   columns,
   data,
   getRowId,
   maxRows,
   viewAllUrl,
   viewAllText = 'View all',
   filterColumnId,
   filterLabel,
   filterPlaceholder,
   pageSize,
   pagerLabels,
   emptyText = 'No results.',
   rowProps,
}: DataTableProps<TData>) {
   const [sorting, setSorting] = useState<SortingState>([]);
   const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

   // Cap through pagination rather than slicing the data, so sorting and
   // filtering still see every row.
   const rowsPerPage = pageSize ?? maxRows;

   const table = useReactTable({
      data,
      columns,
      getRowId,
      defaultColumn: {
         enableSorting: false,
         // v8's stock cell stringifies values; render them so element cells work
         cell: info => renderCellValue(info.renderValue()),
      },
      state: { sorting, columnFilters },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      ...(rowsPerPage != null && {
         getPaginationRowModel: getPaginationRowModel(),
         initialState: { pagination: { pageSize: rowsPerPage } },
      }),
   });

   const filterColumn = filterColumnId ? table.getColumn(filterColumnId) : undefined;
   if (filterColumnId && !filterColumn) {
      console.error(`DataTable: no column with id "${filterColumnId}", so no filter is rendered.`);
   }
   const rows = table.getRowModel().rows;
   const hasMore =
      pageSize == null && maxRows != null && table.getFilteredRowModel().rows.length > maxRows;

   return (
      <div className="flex flex-col gap-4">
         {filterColumn && (
            <Input
               value={(filterColumn.getFilterValue() as string) ?? ''}
               placeholder={filterPlaceholder}
               aria-label={filterLabel}
               className="max-w-sm"
               onChange={event => filterColumn.setFilterValue(event.target.value)}
            />
         )}
         <Table>
            <TableHeader>
               {table.getHeaderGroups().map(headerGroup => (
                  <TableRow key={headerGroup.id}>
                     {headerGroup.headers.map(header => (
                        <TableHead
                           key={header.id}
                           className={header.column.columnDef.meta?.className}
                           aria-sort={ariaSort(header.column)}
                        >
                           {/* p/-m pair: room for the sort toggle's focus ring, which truncate would clip */}
                           <div className="truncate p-[3px] -m-[3px]">
                              {header.isPlaceholder
                                 ? null
                                 : flexRender(header.column.columnDef.header, header.getContext())}
                           </div>
                        </TableHead>
                     ))}
                  </TableRow>
               ))}
            </TableHeader>
            <TableBody>
               {rows.length === 0 ? (
                  <TableRow>
                     <TableCell
                        colSpan={table.getVisibleLeafColumns().length}
                        className="text-center text-muted-foreground"
                     >
                        {emptyText}
                     </TableCell>
                  </TableRow>
               ) : (
                  rows.map(row => {
                     const { className: extraClassName, ...extraProps } =
                        rowProps?.(row.original) ?? {};
                     return (
                        <TableRow
                           key={row.id}
                           className={cn(ROW_LINK_CLASSES, extraClassName)}
                           {...extraProps}
                        >
                           {row.getVisibleCells().map(cell => (
                              <TableCell
                                 key={cell.id}
                                 className={cn(
                                    !cell.column.columnDef.meta?.interactive &&
                                       STRETCHED_LINK_CELL_CLASSES,
                                    cell.column.columnDef.meta?.wrap && 'break-words',
                                    cell.column.columnDef.meta?.className
                                 )}
                              >
                                 {cell.column.columnDef.meta?.wrap ? (
                                    flexRender(cell.column.columnDef.cell, cell.getContext())
                                 ) : (
                                    <div className="truncate">
                                       {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </div>
                                 )}
                              </TableCell>
                           ))}
                        </TableRow>
                     );
                  })
               )}
            </TableBody>
         </Table>
         {hasMore && viewAllUrl && (
            <div>
               <a
                  href={viewAllUrl}
                  className="text-primary hover:text-brand-600 hover:underline dark:hover:text-brand-300"
               >
                  {viewAllText}
               </a>
            </div>
         )}
         {pageSize != null && table.getPageCount() > 1 && (
            <DataTablePager table={table} labels={pagerLabels} />
         )}
      </div>
   );
}

function DataTablePager<TData>({
   table,
   labels = {},
}: {
   table: TanstackTable<TData>;
   labels?: DataTablePagerLabels;
}) {
   const previousText = labels.previous ?? DEFAULT_PAGER_LABELS.previous;
   const nextText = labels.next ?? DEFAULT_PAGER_LABELS.next;
   const pageText = labels.page ?? DEFAULT_PAGER_LABELS.page;
   return (
      <div className="flex items-center gap-3">
         <Button
            type="button"
            className="button-secondary button-md"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
         >
            {previousText}
         </Button>
         <span className="text-sm text-muted-foreground">
            {pageText(table.getState().pagination.pageIndex + 1, table.getPageCount())}
         </span>
         <Button
            type="button"
            className="button-secondary button-md"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
         >
            {nextText}
         </Button>
      </div>
   );
}

export {
   ariaSort,
   DataTable,
   DataTableColumnHeader,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableHeader,
   TableRow,
};
export type { DataTableProps, DataTablePagerLabels };
